import React, { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import StatusPill from '../shared/StatusPill';
import { SelectField, TextField, SegmentedField } from '../shared/FormField';
import { ECOLOGICAL_ZONES } from '../data/enums';
import { useLocationOptions } from '../shared/useLocationOptions';
import { geospatialApi } from '../../../api/geospatialApi';
import { formatDateTime, orDash } from '../shared/format';

function formatActor(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return value.name ?? value.label ?? value.uuid ?? null;
  return String(value);
}

function formatActorAt(actor, at) {
  const who = formatActor(actor);
  const when = at ? formatDateTime(at) : null;
  if (!who && !when) return '—';
  if (who && when) return `${who} — ${when}`;
  return who || when;
}

function DetailLine({ label, value }) {
  return (
    <div className="grid gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
      <span className="text-xs text-gray-700">{value}</span>
    </div>
  );
}

// This module has no originator-vs-reviewer split today: only R01 (System Administrator)
// can reach /admin/geospatial at all (see canAccessModule() in rbacConfig.js). R01 is
// therefore treated as the "originator" role for the Save-as-draft / Submit-for-review
// split described in the standardization pass — see report for details.
export default function EditGeospatialModal({ isOpen, onClose, onSave, onDelete, row }) {
  const [form, setForm]      = useState({
    state: '', lga: '', ward: '', ecological_zone: '', coordinates: '',
    data_status: 'draft', rejection_notes: '',
  });
  const [status, setStatus]  = useState('idle');
  const [errMsg, setErrMsg]  = useState('');
  const [submitting, setSub] = useState(false);
  const [resultInfo, setResultInfo] = useState({ title: undefined, message: undefined });
  const { stateOptions, lgaOptions, wardOptions, loadingLgas, loadingWards } = useLocationOptions(form.state, form.lga);

  const set = (k) => (v) => setForm((f) => {
    if (k === 'state') {
      return { ...f, state: v, lga: '', ward: '' };
    }
    if (k === 'lga') {
      return { ...f, lga: v, ward: '' };
    }
    return { ...f, [k]: v };
  });

  useEffect(() => {
    if (row) {
      setForm({
        state:           row.state           ?? '',
        lga:             row.lga             ?? '',
        ward:            row.ward            ?? '',
        ecological_zone: row.ecological_zone ?? '',
        coordinates:     row.coordinates     ?? '',
        data_status:     (row.data_status ?? 'draft').toLowerCase(),
        rejection_notes: row.rejection_notes ?? '',
      });
      setStatus('idle');
      setErrMsg('');
    }
  }, [row]);

  const currentStatus = (row?.data_status ?? 'draft').toLowerCase();
  // Only a draft/rejected record can meaningfully be "submitted for review".
  const canSubmitForReview = ['draft', 'rejected'].includes(currentStatus);

  const submit = async () => {
    if (!form.state || !row?.uuid) return;

    setSub(true);
    try {
      const updated = await geospatialApi.update(row.uuid, form);
      onSave(updated);
      setResultInfo({
        title: 'Changes Saved',
        message: 'Field changes were saved. The review status was not changed — use Submit for Review to advance it.',
      });
      setStatus('success');
    } catch (e) {
      const msg = e?.response?.data?.message ?? e.message ?? 'Failed to update geospatial record.';
      setErrMsg(msg);
      setStatus('error');
    } finally {
      setSub(false);
    }
  };

  // Explicit, separate action — never triggered implicitly by the plain Save button above.
  const handleSubmitForReview = async () => {
    if (!form.state || !row?.uuid || submitting) return;

    setSub(true);
    try {
      await geospatialApi.update(row.uuid, form);
      const submitted = await geospatialApi.submit(row.uuid);
      onSave(submitted);
      setResultInfo({
        title: 'Submitted for Review',
        message: 'Your changes were saved and this record was submitted for review.',
      });
      setStatus('success');
    } catch (e) {
      const msg = e?.response?.data?.message ?? e.message ?? 'Failed to submit geospatial record for review.';
      setErrMsg(msg);
      setStatus('error');
    } finally {
      setSub(false);
    }
  };

  const handleClose = () => { setStatus('idle'); setErrMsg(''); onClose(); };

  return (
    <Modal
      title="Edit Geospatial Reference"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle={resultInfo.title}
      successMessage={resultInfo.message}
      isSubmitting={submitting}
    >
      <SelectField
        label="State"
        required
        value={form.state}
        onChange={set('state')}
        options={stateOptions}
        placeholder="Select State"
      />
      <TextField
        label="LGA"
        value={form.lga}
        onChange={set('lga')}
        suggestions={lgaOptions}
        placeholder={form.state ? (loadingLgas ? 'Loading LGAs...' : 'Type or select LGA') : 'Select State first'}
        disabled={!form.state}
        note={form.state ? 'Type a new LGA or choose one from the live suggestions.' : 'Select a state first.'}
      />
      <TextField
        label="Ward"
        value={form.ward}
        onChange={set('ward')}
        suggestions={wardOptions}
        placeholder={form.state && form.lga ? (loadingWards ? 'Loading Wards...' : 'Type or select Ward') : 'Select LGA first'}
        disabled={!form.state || !form.lga}
        note={form.state && form.lga ? 'Type a new Ward or choose one from the live suggestions.' : 'Select an LGA first.'}
      />
      <SegmentedField label="Ecological Zone" value={form.ecological_zone} onChange={set('ecological_zone')} options={ECOLOGICAL_ZONES} />
      <TextField
        label="Coordinates"
        value={form.coordinates}
        onChange={set('coordinates')}
        placeholder="lat, lng  e.g. 6.4474, 3.4214"
      />

      {row?.uuid && (
        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-gray-900">Current Status</p>
              <p className="mt-1 text-xs text-gray-500">
                Saving here only updates the fields above — it never changes the review status.
              </p>
            </div>
            <StatusPill value={form.data_status} dot />
          </div>
        </div>
      )}

      {currentStatus === 'rejected' && row?.rejection_notes && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-red-700">Rejection Reason</p>
          <p className="mt-1 text-sm text-red-800">{row.rejection_notes}</p>
        </div>
      )}

      {row?.uuid && (
        <div className="grid gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 sm:grid-cols-2">
          <p className="text-[13px] font-semibold text-gray-900 sm:col-span-2">Record Trail</p>
          <DetailLine label="Boundary Polygon" value={orDash(row?.boundary_polygon)} />
          <DetailLine label="Submitted" value={formatActorAt(row?.submitted_by, row?.submitted_at)} />
          <DetailLine label="Validated" value={formatActorAt(row?.validated_by, row?.validated_at)} />
          <DetailLine label="Approved" value={formatActorAt(row?.approved_by, row?.approved_at)} />
          <DetailLine label="Rejected At" value={row?.rejected_at ? formatDateTime(row.rejected_at) : '—'} />
          <DetailLine label="Created At" value={row?.created_at ? formatDateTime(row.created_at) : '—'} />
          <DetailLine label="Updated At" value={row?.updated_at ? formatDateTime(row.updated_at) : '—'} />
        </div>
      )}

      {row?.uuid && canSubmitForReview && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-blue-900">Ready for review?</p>
            <p className="mt-1 text-xs text-blue-700">Saves your changes, then submits this record for review.</p>
          </div>
          <button
            type="button"
            onClick={handleSubmitForReview}
            disabled={submitting || !form.state}
            className="h-11 shrink-0 rounded-full bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        </div>
      )}
    </Modal>
  );
}
